import { useEffect, useState } from "react";
import { mediaUrl, isHostedVideo, fetchIngredientImages, fetchUtensilImages } from "@/lib/api";
import {
  Clock, Users, Leaf, Fish, Milk, Wheat, Flame, Snowflake, Croissant, Utensils, CookingPot,
} from "lucide-react";

const AISLE = {
  "Produce": { icon: Leaf, tint: "bg-black/5 text-nerd-light" },
  "Meat & Seafood": { icon: Fish, tint: "bg-coral/15 text-coral" },
  "Dairy": { icon: Milk, tint: "bg-sky/15 text-sky" },
  "Pantry": { icon: Wheat, tint: "bg-sand/15 text-sand" },
  "Spices": { icon: Flame, tint: "bg-nerd-light/15 text-orchid" },
  "Bakery": { icon: Croissant, tint: "bg-sand/15 text-sand" },
  "Frozen": { icon: Snowflake, tint: "bg-sky/15 text-sky" },
  "Other": { icon: Utensils, tint: "bg-black/8 text-nerd-light/70" },
};

const fmt = (n) => (Number.isInteger(n) ? n : parseFloat(Number(n).toFixed(2)));

function NutritionCard({ macros = {}, servingSize }) {
  const DV = { fat: 78, sat: 20, sodium: 2300, carbs: 275, fiber: 28, protein: 50 };
  const pct = (v, d) => Math.round(((v || 0) / d) * 100);
  const Row = ({ label, value, dv, indent, last }) => (
    <div className={`flex items-center justify-between py-3 ${last ? "" : "border-b border-black/8"} ${indent ? "pl-5" : ""}`}>
      <span className="text-nerd-light/90">{label} <span className="text-nerd-light">{value}</span></span>
      {dv != null && <span className="text-nerd-light/80 font-medium">{dv}%</span>}
    </div>
  );
  return (
    <div className="bg-nerd-card border border-black/8 rounded-3xl p-6">
      <h3 className="font-display text-2xl font-extrabold tracking-tight">Nutrition Estimates</h3>
      <p className="text-nerd-light/40 text-sm mt-1">Per serving • {servingSize || "1 serving"}</p>
      <div className="h-px bg-black/25 my-4" />
      <div className="flex items-end justify-between">
        <span className="text-nerd-light/90 text-lg font-medium">Calories</span>
        <span className="font-display text-5xl font-extrabold leading-none">{macros.calories || 0}</span>
      </div>
      <p className="text-right text-nerd-light/40 text-sm mt-1 mb-2">% DV</p>
      <Row label="Total Fat" value={`${macros.fat_g || 0}g`} dv={pct(macros.fat_g, DV.fat)} />
      {macros.saturated_fat_g > 0 && <Row label="Saturated Fat" value={`${macros.saturated_fat_g}g`} dv={pct(macros.saturated_fat_g, DV.sat)} indent />}
      {macros.sodium_mg > 0 && <Row label="Sodium" value={`${macros.sodium_mg}mg`} dv={pct(macros.sodium_mg, DV.sodium)} />}
      <Row label="Total Carbohydrate" value={`${macros.carbs_g || 0}g`} dv={pct(macros.carbs_g, DV.carbs)} />
      {macros.fiber_g > 0 && <Row label="Dietary Fiber" value={`${macros.fiber_g}g`} dv={pct(macros.fiber_g, DV.fiber)} indent />}
      {macros.sugars_g > 0 && <Row label="Total Sugars" value={`${macros.sugars_g}g`} indent />}
      <Row label="Protein" value={`${macros.protein_g || 0}g`} dv={pct(macros.protein_g, DV.protein)} last />
    </div>
  );
}

// Read-only recipe view, mirroring the RecipeDetail page — used to preview a
// recipe blueprint exactly as the end user would see it in the app.
export default function RecipePreview({ recipe, videoUrl, thumbnail, title, description, creatorName, creatorHandle }) {
  const [ingImages, setIngImages] = useState({});
  const [utImages, setUtImages] = useState({});
  useEffect(() => {
    const names = (recipe?.ingredients || []).map((x) => x.name).filter(Boolean);
    if (names.length) fetchIngredientImages(names).then(setIngImages).catch(() => {});
    const uts = (recipe?.utensils || []).filter(Boolean);
    if (uts.length) fetchUtensilImages(uts).then(setUtImages).catch(() => {});
  }, [recipe]);
  if (!recipe) return <p className="text-nerd-light/40 text-sm">No recipe to preview.</p>;

  const rTitle = title || recipe.title || "Untitled recipe";
  const rDesc = description || recipe.description || "";
  const handle = creatorHandle || `@${(creatorName || "flavourai").toLowerCase().replace(/\s/g, "")}`;
  const hasVideo = isHostedVideo(videoUrl);
  const cover = thumbnail || recipe.thumbnail;

  const facts = [
    `${recipe.total_time_min || 0} min`,
    `${recipe.ingredients?.length || 0} ingredients`,
    `${recipe.steps?.length || 0} steps`,
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start" data-testid="recipe-preview">
      {/* LEFT — media */}
      <div className="lg:col-span-5 lg:sticky lg:top-6 lg:self-start space-y-5">
        <div className="relative aspect-[4/5] overflow-hidden rounded-3xl bg-nerd-lift">
          {hasVideo ? (
            <video autoPlay loop muted playsInline className="w-full h-full object-cover" src={mediaUrl(videoUrl)} />
          ) : cover ? (
            <img src={cover} alt={rTitle} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-nerd-light/30"><CookingPot className="w-10 h-10" /></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-white rounded-full pl-1 pr-3 py-1 shadow-sm">
            <span className="w-6 h-6 rounded-full bg-nerd-light text-white flex items-center justify-center text-[11px] font-bold">
              {(creatorName || "F").charAt(0)}
            </span>
            <span className="text-nerd-light text-sm font-medium">{handle}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-nerd-light/80 bg-black/5 px-3 py-1.5 rounded-full">
            <Users className="w-3.5 h-3.5" /> {recipe.servings || 1} servings
          </span>
          {recipe.difficulty && (
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-nerd-light/80 bg-black/5 px-3 py-1.5 rounded-full">{recipe.difficulty}</span>
          )}
        </div>
      </div>

      {/* RIGHT — details */}
      <div className="lg:col-span-7 space-y-8 min-w-0">
        <header>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.05] mb-3">{rTitle}</h1>
          <p className="text-nerd-light/60 leading-relaxed max-w-2xl">{rDesc}</p>
          <div className="flex flex-wrap gap-2 mt-4">
            {facts.map((f) => (
              <span key={f} className="inline-flex items-center gap-1.5 text-sm font-medium text-nerd-light/80 bg-black/5 px-3 py-1.5 rounded-full">{f}</span>
            ))}
          </div>
        </header>

        <NutritionCard macros={recipe.macros} servingSize={recipe.serving_size} />

        {/* Ingredients */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <h2 className="font-display text-2xl font-extrabold tracking-tight">Ingredients</h2>
            <span className="w-7 h-7 rounded-full bg-nerd-light text-white text-xs font-bold flex items-center justify-center">{recipe.ingredients?.length || 0}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {(recipe.ingredients || []).map((ing, i) => {
              const meta = AISLE[ing.aisle] || AISLE.Other;
              const Icon = meta.icon;
              const imgUrl = ingImages[(ing.name || "").trim().toLowerCase()];
              return (
                <div key={i} className="w-full flex items-center gap-4 p-3 rounded-2xl border bg-nerd-card border-black/8">
                  {imgUrl
                    ? <div className="shrink-0 w-14 h-14 rounded-2xl overflow-hidden bg-nerd-lift"><img src={mediaUrl(imgUrl)} alt={ing.name} className="w-full h-full object-cover" /></div>
                    : <div className={`shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center ${meta.tint}`}><Icon className="w-6 h-6" /></div>}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate text-nerd-light">{ing.name}</p>
                    <p className="text-sm text-nerd-light/50">{`${fmt(ing.quantity || 0)} ${ing.unit || ""}`.trim()}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Utensils */}
        {(recipe.utensils || []).length > 0 && (
          <section>
            <div className="flex items-center gap-3 mb-4">
              <h2 className="font-display text-2xl font-extrabold tracking-tight">Utensils</h2>
              <span className="w-7 h-7 rounded-full bg-nerd-light text-white text-xs font-bold flex items-center justify-center">{recipe.utensils.length}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {recipe.utensils.map((u, i) => {
                const utImg = utImages[(u || "").trim().toLowerCase()];
                return (
                  <div key={i} className="w-full flex items-center gap-4 p-3 rounded-2xl border bg-nerd-card border-black/8">
                    {utImg
                      ? <div className="shrink-0 w-14 h-14 rounded-2xl overflow-hidden bg-nerd-lift"><img src={mediaUrl(utImg)} alt={u} className="w-full h-full object-cover" /></div>
                      : <div className="shrink-0 w-14 h-14 rounded-2xl bg-black/5 text-nerd-light flex items-center justify-center"><CookingPot className="w-6 h-6" /></div>}
                    <p className="font-medium text-nerd-light">{u}</p>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Steps */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <h2 className="font-display text-2xl font-extrabold tracking-tight">Steps</h2>
            <span className="w-7 h-7 rounded-full bg-nerd-light text-white text-xs font-bold flex items-center justify-center">{recipe.steps?.length || 0}</span>
          </div>
          <div className="space-y-2.5">
            {(recipe.steps || []).map((s, i) => (
              <div key={i} className="flex gap-4 p-4 rounded-2xl bg-nerd-card border border-black/8">
                <div className="shrink-0 w-14 h-14 rounded-2xl bg-black/5 text-nerd-light flex items-center justify-center font-mono-x text-lg font-semibold">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="flex-1 pt-0.5">
                  <p className="font-medium text-nerd-light leading-relaxed">{s.instruction || s}</p>
                  {s.duration_min > 0 && (
                    <p className="text-nerd-light/40 text-sm mt-1.5 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {s.duration_min} mins</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {recipe.tip && (
          <div className="bg-black/5 border border-black/8 rounded-2xl p-5">
            <p className="text-xs font-mono uppercase tracking-widest text-nerd-light/40 mb-1.5">Chef's tip</p>
            <p className="text-nerd-light/80 text-sm leading-relaxed">{recipe.tip}</p>
          </div>
        )}
      </div>
    </div>
  );
}
